import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { useResidentEnvironments } from '~/hooks/environment/useResidentEnvironments';
import { Environment } from '~/engine/data/Environment';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import {
    selectedROIState,
    structureIsolationActiveState,
} from '~/state/atoms/structureSet/roiAtoms';

function repaintContourLayer(environment: Environment): void {
    ViewManager.invalidate(
        LayerBit.CONTOUR_LAYER_BIT,
        environment.structureSet
    );
}

/**
 * Writes the flag onto one Environment. Repaint only while isolate is
 * involved, so a user who never turns it on pays nothing for it, and only
 * where a structure set is drawn: the flag still lands on an image-only
 * Environment for the set it may draw later, but a repaint scoped to no
 * volume would mark every view. Invalidation is per-frame and idempotent,
 * so this costs no second redraw when the selection change already
 * invalidated the layer; it is what keeps the spotlight following a
 * selection moved by a path that does not go through Environment.
 */
function publishIsolation(environment: Environment, active: boolean): void {
    const wasActive = environment.structureIsolationActive;
    environment.structureIsolationActive = active;
    if ((wasActive || active) && environment.structureSet) {
        repaintContourLayer(environment);
    }
}

/**
 * Bridges the soft-isolate flag onto every resident Environment and repaints
 * their contour layers. Only those Environments hold the flag: one that
 * leaves the resident set is lifted at once, and every one still dimmed is
 * lifted on unmount. Display-only, so there is nothing to snapshot or
 * restore. Mount once in an always-rendered viewer component that outlives
 * the grid's tiles.
 */
export function useStructureIsolation(): void {
    const environments = useResidentEnvironments();
    const isolationActive = useRecoilValue(structureIsolationActiveState);
    const selectedRoiId = useRecoilValue(selectedROIState);
    // The Environments this hook has set to dim and not yet lifted.
    const dimmedRef = useRef(new Set<Environment>());

    // selectedRoiId is a dep: moving the selection changes which contour
    // renders at full alpha, so it must repaint. No cleanup on purpose: it
    // would double the repaints when stepping the list (a hot path); the
    // body overwrites the flag on each run.
    useEffect(() => {
        for (const environment of dimmedRef.current) {
            if (!environments.includes(environment)) {
                publishIsolation(environment, false);
            }
        }
        for (const environment of environments) {
            publishIsolation(environment, isolationActive);
        }
        dimmedRef.current = new Set(isolationActive ? environments : []);
    }, [environments, isolationActive, selectedRoiId]);

    // Reset on unmount only, so a teardown mid-isolate cannot leave the
    // engine dimming for the next mount; the ref keeps this effect dep-free.
    useEffect(
        () => () => {
            const dimmed = dimmedRef.current;
            for (const environment of dimmed) {
                publishIsolation(environment, false);
            }
            dimmed.clear();
        },
        []
    );
}
